import moment from 'moment';
import { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import { Box, IconButton, useTheme, Theme, Typography } from '@mui/material';

import {
  handleGetCaseTypes,
  handleGetDefendants,
  handleGetPlaintiffs,
  handleGetTrustAccounts,
  mapCaseTypesQueryResults,
  mapDefendantsQueryResults,
  mapPlaintiffsQueryResults,
  mapTrustAccountsQueryResults,
} from './helpers';
import { CaseCreateFormSubmitProps, DefendantField } from './types';

import { createCase } from '../../api/casesApi/casesApi';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormDateField from '../../components/formFields/FormDateField';
import FormTextField from '../../components/formFields/FormTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import { CASES_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import { casesFormSchema } from '../../helpers/formSchemas/formSchemas';
import AddButton from '../../components/buttons/AddButton';
import FormAutocompleteFieldServerSearch from '../../components/formFields/FormAutocompleteFieldServerSearch';
import useSnackBar from '../../hooks/useSnackBar';
import useWindowWidth from '../../hooks/useWindowWidth';
import { useHasPermission } from '../../hooks/useHasPermission';
import { CASE_TYPES_CATEGORIES_OPTIONS } from '../../constants/constants';
import {
  CaseTypeCategory,
  CaseTypeData,
  PaginationQueryParams,
  SelectOption,
} from '../../types';

const useStyles = makeStyles()((theme: Theme) => ({
  caseCreateForm: {
    padding: '32px',
    flexBasis: '896px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 1100px)': {
      flexBasis: 'auto',
    },

    '@media (max-width: 725px)': {
      flexBasis: '520px',
      padding: '16px',
    },
  },
  formContainer: {
    display: 'flex',
    width: '100%',
    gap: '32px',

    '@media (max-width: 1100px)': {
      flexDirection: 'column',
    },
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  dateFieldsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',

    '@media (max-width: 725px)': {
      flexDirection: 'column',
      gap: '24px',
    },
  },
  dateFieldWrapper: {
    width: '100%',
  },
  balanceFieldsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',

    '@media (max-width: 725px)': {
      flexDirection: 'column',
      gap: '24px',
    },
  },
  defendantFieldsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',

    '@media (max-width: 725px)': {
      flexDirection: 'column',
    },
  },
  formWrapper: {
    display: 'flex',
  },
}));

const CasesCreateFormContainer = () => {
  const styles = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const windowWidth = useWindowWidth();

  const firmId = localStorage.getItem('firmId');

  const canCreateClients = useHasPermission('Clients', 'Create');
  const canCreateDefendants = useHasPermission('Defendants', 'Create');

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(casesFormSchema),
    defaultValues: { ...CASES_FORM_DEFAULT_VALUES, firmId: firmId || '' },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = formMethods;

  const caseTypeCategory = watch('caseTypeCategory');
  const type = watch('type');

  const { fields, append, remove } = useFieldArray({
    name: 'defendant',
    control,
  });

  const handleAddDefendant = () => {
    append({
      defendantId: { id: '', label: '' },
      gross: '',
    });
  };

  const onSubmitForm = (values: CaseCreateFormSubmitProps) => {
    setIsFormSubmitting(true);

    const mappedDefendants = values?.defendant?.map((defendant: DefendantField) => ({
      id: Number(defendant.defendantId.id),
      grossSettlement: defendant.gross ? Number(defendant.gross) : null,
    }));

    const submitData = {
      referenceId: values.referenceId,
      clientId: Number(values.clientId.id),
      ...(mappedDefendants && { defendants: mappedDefendants }),
      caseTypeId: Number(values.type.id),
      ...(values.filedAt && { filedAt: moment(values.filedAt).format('MM-DD-YYYY') }),
      ...(values.sol && { sol: moment(values.sol).format('MM-DD-YYYY') }),
      ...(values.trustAccount?.id && { trustAccountId: Number(values.trustAccount.id) }),
    };

    createCase(submitData)
      .then((res) => {
        setIsFormSubmitting(false);
        navigate(`/cases/${res.data.id}/home`);

        setAlert((prev) => ({
          ...prev,
          message: 'Case created successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setIsFormSubmitting(false);
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleCancelChanges = () => reset();

  const handleChangeCaseTypeCategoryValue = (value: string) => {
    setValue('caseTypeCategory', value, { shouldDirty: true, shouldValidate: true });
    setValue('type', { id: '', label: '' }, { shouldDirty: true, shouldValidate: true });
    setValue('trustAccount', { id: '', label: '' }, { shouldDirty: true, shouldValidate: true });
  };

  const handleChangeCaseTypeValue = (value: SelectOption | null) => {
    if (!value) {
      setValue('trustAccount', { id: '', label: '' }, { shouldDirty: true, shouldValidate: true });
      return;
    }

    // Fetch case types to get trustAccounts data
    handleGetCaseTypes({
      page: 1,
      size: 100,
      count: true,
      match: '',
      filters: {
        category: caseTypeCategory,
        ...(caseTypeCategory === CaseTypeCategory.Custom && { firmId: Number(firmId) }),
      },
    }).then((res) => {
      const caseTypesData = res?.data?.resultSet || [];
      const caseType = caseTypesData.find((ct: CaseTypeData) => ct.id === Number(value.id));

      if (caseType && caseType.trustAccounts && caseType.trustAccounts.length) {
        const trustAccount = caseType.trustAccounts.find((ta) => ta.firmId === Number(firmId));
        if (trustAccount) {
          setValue('trustAccount', { id: trustAccount.id.toString(), label: trustAccount.name }, { shouldDirty: true, shouldValidate: true });
        }
      }
    });
  };

  return (
    <>
      <Box className={styles.classes.formWrapper}>
        <FormProvider {...formMethods}>
          <form className={styles.classes.caseCreateForm}>
            <Box className={styles.classes.formContainer}>
              <Box display="flex" flexDirection="column" width="100%" maxWidth="470px">
                <Typography className={styles.classes.fieldHeadline}>Identity</Typography>
                <Box mt="16px">
                  <FormTextField name="referenceId" label="Reference Id *" />
                </Box>
                <Box mt="24px">
                  <FormSelectField
                    options={CASE_TYPES_CATEGORIES_OPTIONS}
                    width="100%"
                    name="caseTypeCategory"
                    label="Case Type Category *"
                    customOnChange={handleChangeCaseTypeCategoryValue}
                  />
                </Box>
                <Box mt="24px">
                  <FormAutocompleteFieldServerSearch
                    key={`${caseTypeCategory ?? 'none'}-${firmId ?? 'no-firm'}`}
                    disabled={!caseTypeCategory}
                    name="type"
                    label="Case Type *"
                    width="100%"
                    filters={{
                      category: caseTypeCategory,
                      ...(caseTypeCategory === CaseTypeCategory.Custom && {
                        firmId: Number(firmId),
                      }),
                    }}
                    optionsQuery={handleGetCaseTypes}
                    mapQueryResults={mapCaseTypesQueryResults}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    onValueChange={handleChangeCaseTypeValue}
                    fetchSize={100}
                    minSearchChars={3}
                  />
                </Box>

                <Typography className={styles.classes.fieldHeadline} mt="32px">
                  Trust Account
                </Typography>
                <Box mt="16px">
                  <FormAutocompleteFieldServerSearch
                    key={type?.id ? String(type.id) : 'no-type'}
                    disabled={!type?.id}
                    label="Trust Account *"
                    name="trustAccount"
                    width="100%"
                    optionsQuery={(params: PaginationQueryParams) => {
                      return handleGetTrustAccounts(params);
                    }}
                    mapQueryResults={mapTrustAccountsQueryResults}                   
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    fetchSize={100}
                    minSearchChars={3}
                  />
                </Box>

                <Typography className={styles.classes.fieldHeadline} mt="32px">
                  Key Dates
                </Typography>
                <Box className={styles.classes.dateFieldsContainer}>
                  <Box width="100%">
                    <FormDateField name="filedAt" label="Date Filed" />
                  </Box>
                  <Box width="100%">
                    <FormDateField name="sol" label="SOL Date" />
                  </Box>
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" width="100%" maxWidth="520px">
                <Typography className={styles.classes.fieldHeadline}>Plaintiff</Typography>
                <Box mt="16px">
                  <FormAutocompleteFieldServerSearch
                    maxWidth="468px"
                    name="clientId"
                    label="Client *"
                    width="100%"
                    optionsQuery={handleGetPlaintiffs}
                    mapQueryResults={mapPlaintiffsQueryResults}
                    filters={{
                      firmId: Number(firmId),
                    }}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    fetchSize={100}
                    minSearchChars={3}
                  />
                </Box>
                {canCreateClients && (
                  <Box mt="16px" display="flex">
                    <AddButton buttonText="Create new client" path="/clients/create-client" />
                  </Box>
                )}

                <Typography className={styles.classes.fieldHeadline} mt="32px">
                  Defendants
                </Typography>

                {fields.map((field, idx) => (
                  <Box className={styles.classes.defendantFieldsContainer} key={field.id}>
                    <FormAutocompleteFieldServerSearch
                      name={`defendant.${idx}.defendantId`}
                      label="Defendant *"
                      maxWidth={windowWidth <= 725 ? '100%' : '468px'}
                      width="100%"
                      optionsQuery={handleGetDefendants}
                      mapQueryResults={mapDefendantsQueryResults}
                      filters={{
                        firmId: Number(firmId),
                      }}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                      fetchSize={100}
                      minSearchChars={3}
                    />

                    {/* Gross settlement is no longer captured at case creation; hidden for now. */}
                    {/* <FormTextField
                      name={`defendant.${idx}.gross`}
                      label="Gross Settlement"
                      maxWidth={windowWidth <= 725 ? '100%' : '180px'}
                    /> */}
                    {idx !== 0 && (
                      <Box display="flex" width={windowWidth <= 725 ? '100%' : 'inherit'} gap="16px">
                        <IconButton
                          sx={{
                            padding: 0,
                            color: theme.palette.error.main,
                          }}
                          onClick={() => remove(idx)}
                        >
                          <DeleteForeverIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}

                <Box
                  mt="16px"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  maxWidth="468px"
                  gap="16px"
                >
                  {canCreateDefendants && (
                    <AddButton
                      buttonText="Create new defendant"
                      path="/defendants/create-defendant"
                    />
                  )}
                  <AddButton buttonText="Add defendant" handleClick={handleAddDefendant} />
                </Box>
              </Box>
            </Box>

            <Box display="flex" width="100%" justifyContent="center">
              <Box
                display="flex"
                mt="32px"
                maxWidth="416px"
                flexDirection="column"
                gap="16px"
                width="100%"
              >
                <FormSubmitButton
                  disabled={!isValid || isFormSubmitting}
                  buttonIcon={<SaveIcon />}
                  buttonText="Save"
                  onClick={handleSubmit(onSubmitForm)}
                />
                <FormCancelButton disabled={!isDirty} onClick={handleCancelChanges} />
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </>
  );
};

export default CasesCreateFormContainer;
